/**
 * ErrorBoundary - Manejo de errores a nivel de componente
 *
 * Captura errores en el arbol de componentes y muestra una UI amigable.
 * Diseñado para neurodivergencia:
 * - Mensajes claros y sin jerga tecnica
 * - Acciones simples y obvias
 * - Sin culpa ni estres adicional
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/ui/primitives';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log del error para debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Callback opcional para analytics/logging externo
    this.props.onError?.(error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = (): void => {
    // Limpiar estado y volver al inicio
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // Si hay un fallback custom, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI por defecto - amigable y sin estres
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-slate-950">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Icono */}
            <div className="flex justify-center">
              <div className="p-4 rounded-full bg-amber-900/30 border border-amber-700">
                <AlertTriangle className="w-10 h-10 text-amber-400" />
              </div>
            </div>

            {/* Mensaje principal */}
            <div className="space-y-2">
              <h1 className="text-xl font-semibold text-slate-100">
                Algo salio mal
              </h1>
              <p className="text-sm text-slate-400">
                No te preocupes, no es tu culpa. A veces las apps tienen dias dificiles tambien.
              </p>
            </div>

            {/* Opciones de accion */}
            <div className="space-y-3">
              <Button
                variant="primary"
                size="md"
                onClick={this.handleReload}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Recargar la app
              </Button>

              <Button
                variant="ghost"
                size="md"
                onClick={this.handleGoHome}
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </div>

            {/* Info tecnica colapsable (para debugging) */}
            <details className="text-left">
              <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-500">
                Detalles tecnicos (para soporte)
              </summary>
              <pre className="mt-2 p-3 bg-slate-900 rounded-lg text-xs text-slate-500 overflow-auto max-h-32">
                {this.state.error?.message || 'Error desconocido'}
              </pre>
            </details>

            {/* Mensaje de apoyo */}
            <p className="text-xs text-slate-600">
              Tus datos estan guardados localmente y no se han perdido.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorBoundary especifico para secciones de la app
 * Muestra un error mas compacto sin ocupar toda la pantalla
 */
export function SectionErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-6 text-center space-y-4 bg-slate-900/50 rounded-xl border border-slate-800">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
          <div>
            <p className="text-sm text-slate-300">
              Esta seccion tuvo un problema
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Intenta recargar la pagina
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Recargar
          </Button>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
